import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { History as HistoryIcon, ArrowRight, Loader2, CheckCircle2, AlertCircle, MessageSquare, Compass, Megaphone, FileText, Users, FileDown, Hash, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_META = {
  "comment-goldmine": { label: "Comment Goldmine", icon: MessageSquare, route: "/comment-goldmine", color: "text-emerald-400", supportsPdf: true },
  "instagram-trending": { label: "Virale Posts", icon: Compass, route: "/trends?tab=ig-trending", color: "text-violet-400" },
  "facebook-ads-spy": { label: "Facebook Ads Spy", icon: Megaphone, route: "/facebook-tools?tab=ads", color: "text-cyan-400" },
  "facebook-page-audit": { label: "Page Audit", icon: FileText, route: "/facebook-tools?tab=page", color: "text-cyan-400" },
  "facebook-group-listener": { label: "Group Listener", icon: Users, route: "/facebook-tools?tab=group", color: "text-cyan-400" },
  "hashtag-check": { label: "Hashtag-Check", icon: Hash, route: "/hashtag-check", color: "text-cyan-400" },
  "carousel": { label: "Karussell", icon: Layers, route: "/carousel", color: "text-cyan-400" },
};

function formatTimeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  return `vor ${Math.floor(diff / 86400)} Tagen`;
}

function StatusDot({ status }) {
  if (status === "completed") return <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0" />;
  if (status === "running") return <Loader2 className="w-3 h-3 text-cyan-400 animate-spin flex-shrink-0" />;
  return <AlertCircle className="w-3 h-3 text-red-400 flex-shrink-0" />;
}

export default function RecentAnalyses() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tools/history?limit=5`);
        if (!cancelled) setRuns(data.runs || []);
      } catch (_) { if (!cancelled) setRuns([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (runs === null) return null;       // loading — render nothing
  if (runs.length === 0) return null;   // empty — render nothing (don't push other content down)

  const reopen = (run) => {
    const meta = TOOL_META[run.tool];
    if (!meta) return;
    const sep = meta.route.includes("?") ? "&" : "?";
    navigate(`${meta.route}${sep}jobId=${run.id}`);
  };

  return (
    <div className="max-w-2xl mx-auto mb-12 sm:mb-16" data-testid="recent-analyses">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <HistoryIcon className="w-4 h-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Meine letzten Analysen</h3>
        </div>
        <button
          onClick={() => navigate("/analyses")}
          className="text-[11px] text-zinc-400 hover:text-cyan-300 transition-colors flex items-center gap-1"
          data-testid="recent-analyses-all-link"
        >
          Alle anzeigen <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="rounded-2xl bg-zinc-900/40 border border-zinc-800 divide-y divide-zinc-800/70 overflow-hidden">
        {runs.map(r => {
          const meta = TOOL_META[r.tool] || { label: r.tool, icon: HistoryIcon, color: "text-zinc-400" };
          const Icon = meta.icon;
          const clickable = r.status === "completed";
          return (
            <div
              key={r.id}
              onClick={() => clickable && reopen(r)}
              className={`group flex items-center gap-3 p-3 transition-colors ${clickable ? "cursor-pointer hover:bg-zinc-800/40" : "opacity-70"}`}
              data-testid={`recent-row-${r.id}`}
            >
              <div className="w-8 h-8 rounded-lg bg-zinc-800/80 flex items-center justify-center flex-shrink-0">
                <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium text-white truncate">{meta.label}</p>
                  <StatusDot status={r.status} />
                </div>
                <p className="text-[11px] text-zinc-500 truncate">
                  {r.input_label || "—"} · {formatTimeAgo(r.created_at)}
                </p>
              </div>
              {meta.supportsPdf && r.status === "completed" && (
                <a
                  href={`${API}/tools/jobs/${r.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded-md text-zinc-500 hover:text-cyan-300 hover:bg-zinc-800 transition-colors flex-shrink-0"
                  title="Als PDF speichern"
                  data-testid={`recent-pdf-${r.id}`}
                >
                  <FileDown className="w-3.5 h-3.5" />
                </a>
              )}
              {clickable && (
                <ArrowRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
